/**
 * Compare two strings without leaking, through timing, where they first differ.
 *
 * A plain `===` on strings short-circuits at the first mismatching character,
 * so the time it takes is a function of how much of the value the caller got
 * right — an oracle for anything an attacker is allowed to guess repeatedly
 * (CWE-208). This walks the full length of both inputs and accumulates the
 * difference, so the work done is the same whether the strings match at
 * character 0 or not at all.
 *
 * Length still differs observably; that is inherent to comparing strings and
 * acceptable for the fixed-width digests this is used on.
 */
export function constantTimeEquals(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (a == null || b == null) {
    return a == null && b == null;
  }

  const length = Math.max(a.length, b.length);
  let difference = a.length ^ b.length;

  for (let index = 0; index < length; index += 1) {
    difference |= (a.charCodeAt(index) || 0) ^ (b.charCodeAt(index) || 0);
  }

  return difference === 0;
}
